import {createContext, useContext} from "react";
import {useLocation} from "react-router";
import {Colors} from "@/styles";
import {useDomainThemeQuery, useUserThemeQuery, DomainThemeQuery} from "./queries.generated";
import {CssBaseline, ThemeOptions} from "@mui/material";
import {useStructureConfig} from "./StructureConfigProvider";
import {createTheme, ThemeProvider as MUIThemeProvider, darken} from "@mui/material";
import {ThemeProvider as EmotionThemeProvider} from "@emotion/react";
import {red} from "@mui/material/colors";
import isNull from "lodash/isNull";
import omitBy from "lodash/omitBy";
import set from "lodash/set";
import {useIntl} from "react-intl";
import {huHU, enUS} from "@mui/material/locale";

// To provide compatibility between intl.locale and mui locales, we need a mapping
const intlMuiLocaleMapping = {
    hu: huHU,
    en: enUS,
};

const defaultDomainData: DomainThemeQuery["domainTheme"] = {
    color: window.defaultTheme?.baseColor || Colors.Base,
    background: window.defaultTheme?.backgroundImage,
    backgroundColor: window.defaultTheme?.backgroundColor,
    logo: window.defaultTheme?.logoImage,
};

function parseIdsFromLocation(): [number, number] {
    const location = useLocation();

    const domainMatch = location.pathname.match(/\/domains\/([\d]+)/);
    const extensionMatch = location.pathname.match(/\/personal-extensions\/([\d]+)/);

    return [domainMatch ? parseInt(domainMatch[1]) : 0, extensionMatch ? parseInt(extensionMatch[1]) : 0];
}

const ThemeContext = createContext(undefined);

export function useDevertoTheme(): DomainThemeQuery["domainTheme"] {
    return useContext(ThemeContext);
}

const BaseProvider = ({
    devertoTheme,
    children,
}: {
    devertoTheme: DomainThemeQuery["domainTheme"];
    children: React.ReactNode;
}) => {
    const config = useStructureConfig();
    const intl = useIntl();

    const customTheme = devertoTheme && config.isEnabledFeature("domainBaseColor") ? devertoTheme : {};
    const merged = {...defaultDomainData, ...omitBy(customTheme, isNull)};

    const mainColor = !merged.color ? Colors.Base : merged.color;
    const themeOptions: ThemeOptions = {
        palette: {
            mode: "light",
            primary: {
                main: mainColor,
                dark: darken(mainColor, 0.2),
            },
            secondary: {
                main: red[700],
            },
        },
    };

    if (merged.backgroundColor) {
        set(themeOptions, "palette.background.default", merged.backgroundColor);
    }

    const muiLocale =
        intl.locale === "hu" ? intlMuiLocaleMapping.hu : intl.locale === "en" ? intlMuiLocaleMapping.en : enUS;
    const theme = createTheme(themeOptions, muiLocale);

    return (
        <ThemeContext.Provider value={merged}>
            <MUIThemeProvider theme={theme}>
                {/* for tss-react makeStyles we need to use the emotion theme provider too */}
                <EmotionThemeProvider theme={theme}>
                    <CssBaseline />
                    {children}
                </EmotionThemeProvider>
            </MUIThemeProvider>
        </ThemeContext.Provider>
    );
};

/// Provides the default system theme without trying to load anything.
export const DefaultThemeProvider = ({children}: {children: React.ReactNode}) => {
    return <BaseProvider devertoTheme={null}>{children}</BaseProvider>;
};

/// provides the theme that matches the domain or extension theme deduced from the location URI
export const ThemeProvider = ({children}: {children: React.ReactNode}) => {
    const [domainId, extensionId] = parseIdsFromLocation();
    const domainQuery = useDomainThemeQuery({
        variables: {domainId: domainId},
        skip: domainId == 0,
    });
    const extensionQuery = useUserThemeQuery({
        variables: {userId: extensionId},
        skip: extensionId == 0,
    });

    let theme: DomainThemeQuery["domainTheme"] = null;
    if (domainId > 0) {
        if (!domainQuery.loading && !domainQuery.error) {
            theme = domainQuery.data.domainTheme;
        }
    } else if (extensionId > 0) {
        if (!extensionQuery.loading && !extensionQuery.error) theme = extensionQuery.data.userTheme;
    }

    return <BaseProvider devertoTheme={theme}>{children}</BaseProvider>;
};
